/**
 * Rectorium — Capability Registry Service
 *
 * Source unique de vérité pour toutes les capacités métier.
 * Pilote : navigation, permissions, licences, IA, analytics,
 *          marketplace, dashboards, quotas, workflows.
 *
 * Aucune capacité ne doit être définie ailleurs que dans capabilities.js.
 */

import {
  CAPABILITIES,
  CAPABILITIES_MAP,
  CAPABILITIES_BY_DOMAINE,
  CAPABILITY_DEPENDENCIES,
  PORTAL_TYPES,
  PORTAL_ROUTING_RULES,
} from '../constants/capabilities';

// ── 1. Résolution de portail à partir des capacités ──────────────────────────
/**
 * Détermine le type de portail à afficher pour un utilisateur.
 * @param {(capability: string, action?: string) => boolean} canFn
 * @returns {string} — une valeur de PORTAL_TYPES
 */
export function resolvePortalType(canFn) {
  for (const rule of PORTAL_ROUTING_RULES) {
    if (rule.requires.every(cap => canFn(cap, 'read'))) {
      return rule.portalType;
    }
  }
  return PORTAL_TYPES.LIMITED_PORTAL;
}

// ── 2. Vérification des dépendances ──────────────────────────────────────────
/**
 * Renvoie la liste des capacités qui seraient cassées si `capId` était désactivé.
 * @param {string} capId
 * @param {string[]} activeCaps — IDs des capacités actuellement ACTIVE pour ce tenant
 * @returns {string[]} — IDs des capacités dépendantes qui seraient cassées
 */
export function getDependents(capId, activeCaps) {
  return Object.entries(CAPABILITY_DEPENDENCIES)
    .filter(([, deps]) => deps.includes(capId))
    .map(([cap]) => cap)
    .filter(cap => activeCaps.includes(cap));
}

/**
 * Renvoie les capacités manquantes pour activer `capId`.
 * @param {string} capId
 * @param {string[]} activeCaps
 * @returns {string[]} — IDs des dépendances non satisfaites
 */
export function getMissingDependencies(capId, activeCaps) {
  const required = CAPABILITY_DEPENDENCIES[capId] || [];
  return required.filter(dep => !activeCaps.includes(dep));
}

/**
 * Vérifie si une capacité peut être désactivée sans casser d'autres.
 */
export function canDeactivate(capId, activeCaps) {
  const dependents = getDependents(capId, activeCaps);
  return { safe: dependents.length === 0, blockedBy: dependents };
}

/**
 * Vérifie si toutes les dépendances d'une capacité sont satisfaites.
 */
export function canActivate(capId, activeCaps) {
  const missing = getMissingDependencies(capId, activeCaps);
  return { safe: missing.length === 0, requires: missing };
}

// ── 6. Exports pour accès direct ─────────────────────────────────────────────
export {
  CAPABILITIES,
  CAPABILITIES_MAP,
  CAPABILITIES_BY_DOMAINE,
  CAPABILITY_DEPENDENCIES,
  PORTAL_TYPES,
  PORTAL_ROUTING_RULES,
};
